import { useState, type FormEvent } from 'react';
import { useLoaderData, useNavigate, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import { Button, Input, Label, Modal, Checkbox } from '@immo/ui-kit';
import { UpdateAdminWorkspaceBodySchema, type GetAdminWorkspaceResponse } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { RequirePermission } from '@/lib/auth/permissions';

export async function workspaceEditLoader({ params }: LoaderFunctionArgs): Promise<GetAdminWorkspaceResponse> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing workspace id', { status: 404 });
  }

  const result = await apiClient.adminWorkspaces.get({ pathParams: { id } });

  if (!result.ok) {
    throw new Response('Failed to load workspace', { status: result.status });
  }

  return result.body;
}

export function WorkspaceEditPage() {
  const t = useTranslations('pages.admin.workspaces.edit');
  const workspace = useLoaderData() as GetAdminWorkspaceResponse;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [name, setName] = useState(workspace.name);
  const [isTest, setIsTest] = useState(workspace.isTest);
  const [nameError, setNameError] = useState<string | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The mark has an immediate customer-visible effect (the app-wide banner), so
  // the change is gated behind an explicit confirmation before the PATCH fires.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const markChanged = isTest !== workspace.isTest;

  const close = () => navigate(`/workspaces/${workspace.id}`);

  const revertMark = () => setIsTest(workspace.isTest);

  const persist = async () => {
    setNameError(null);
    setGenericError(null);

    // Send `isTest` only when it changed. Omitted means UNCHANGED on the
    // backend, so a plain rename never clears the mark.
    const parsed = UpdateAdminWorkspaceBodySchema.safeParse(markChanged ? { name, isTest } : { name });

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const key = issue?.code === 'too_small' ? (name.trim().length === 0 ? 'required' : 'too-short') : 'too-long';
      setNameError(t(`errors.name.${key}`));

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminWorkspaces.update({
        pathParams: { id: workspace.id },
        body: parsed.data,
      });

      if (result.ok) {
        revalidator.revalidate();
        navigate(`/workspaces/${workspace.id}`);

        return;
      }

      // The save failed — revert the checkbox so the form never disagrees with
      // the stored state.
      revertMark();
      setGenericError(t(result.status === 403 ? 'forbidden' : 'generic-error'));
    } catch {
      revertMark();
      setGenericError(t('generic-error'));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Only the mark change is confirmed. A pure rename saves straight away.
    if (markChanged) {
      setConfirmOpen(true);

      return;
    }

    void persist();
  };

  const confirmMark = () => {
    setConfirmOpen(false);
    void persist();
  };

  const cancelMark = () => {
    setConfirmOpen(false);
    revertMark();
  };

  return (
    <>
      <Modal
        open
        onClose={close}
        size="md"
        title={t('title')}
        footer={
          <>
            <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
              {t('back-cta')}
            </Button>
            <Button type="submit" form="workspace-edit-form" disabled={submitting}>
              {submitting ? t('submit-loading') : t('submit-cta')}
            </Button>
          </>
        }
      >
        <form id="workspace-edit-form" className="space-y-4" onSubmit={submit} noValidate>
          <div className="space-y-1">
            <Label htmlFor="workspace-name">{t('name-label')}</Label>
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError(null);
              }}
              disabled={submitting}
              invalid={nameError !== null}
              aria-describedby={nameError !== null ? 'workspace-name-error' : undefined}
            />
            {nameError !== null && (
              <p id="workspace-name-error" className="text-sm text-danger-600">
                {nameError}
              </p>
            )}
          </div>
          <RequirePermission permission="backoffice:identity:workspace-update">
            <label className="flex items-center gap-2 text-sm text-text-primary">
              <Checkbox checked={isTest} disabled={submitting} onCheckedChange={(checked) => setIsTest(checked)} />
              {t('is-test-label')}
            </label>
          </RequirePermission>
          {genericError !== null && <p className="text-base text-danger-600">{genericError}</p>}
        </form>
      </Modal>
      {confirmOpen && (
        <Modal
          open
          onClose={cancelMark}
          size="sm"
          title={t(isTest ? 'is-test-confirm-title' : 'is-test-remove-confirm-title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={cancelMark} disabled={submitting}>
                {t('back-cta')}
              </Button>
              <Button type="button" onClick={confirmMark} disabled={submitting}>
                {t('is-test-confirm-cta')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-primary">
            {t(isTest ? 'is-test-confirm-body' : 'is-test-remove-confirm-body')}
          </p>
        </Modal>
      )}
    </>
  );
}
